'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { API_BASE_URL } from '@/lib/api';
import { Category } from '@/types';
import CategoryAutocomplete from '@/components/CategoryAutocomplete';
import HashtagAutocomplete from '@/components/HashtagAutocomplete';

export default function ContributePage() {
  const router = useRouter();
  const [user, setUser] = useState<{ id: string; email: string; name?: string } | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    summary: '',
    sources: ''
  });
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [selectedHashtags, setSelectedHashtags] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [newArticleTitle, setNewArticleTitle] = useState('');

  // Check if user is logged in
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      // Redirect to login if not authenticated
      router.push('/login');
    } else {
      setUser(JSON.parse(userData));
    }
  }, [router]);

  // Show loading while checking authentication
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted with data:', formData);
    setLoading(true);
    setError(null);

    try {
      // Check if user is logged in
      if (!user || !user.id) {
        setError('You must be logged in to contribute');
        setLoading(false);
        return;
      }

      // Parse sources
      const sources = formData.sources.split('\n').map(s => {
        const match = s.match(/^(.+?)\s*\((.+?)\)$/);
        return match ? {
          title: match[1].trim(),
          type: match[2].trim().toLowerCase(),
          url: 'https://example.com' // Placeholder URL
        } : {
          title: s.trim(),
          type: 'web',
          url: 'https://example.com' // Placeholder URL
        };
      }).filter(s => s.title);

      const articleData = {
        title: formData.title,
        content: formData.content,
        summary: formData.summary,
        categories: selectedCategories.map(cat => cat.name),
        hashtags: selectedHashtags,
        sources,
        createdBy: user.id, // Use logged-in user's ID
        status: 'draft'
      };

      // Make direct API call instead of using apiClient
      const response = await fetch(`${API_BASE_URL}/api/articles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(articleData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const newArticle = await response.json();
      
      // Show success dialog
      setNewArticleTitle(newArticle.title);
      setSuccess(true);
      
      // Reset form
      setFormData({
        title: '',
        content: '',
        summary: '',
        sources: ''
      });
      setSelectedCategories([]);
      setSelectedHashtags([]);
    } catch (err) {
      console.error('Error submitting article:', err);
      setError('Failed to submit article. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <nav className="text-sm mb-4">
          <Link href="/" className="text-blue-600 hover:underline">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900">Contribute</span>
        </nav>
        
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Contribute to Grokypedia</h1>
        <p className="text-lg text-gray-600">
          Help us build the world&apos;s most comprehensive knowledge base by contributing articles and improving existing content.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2">
          <div className="bg-white border rounded-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Submit a New Article</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                  Article Title *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter the article title"
                  required
                />
              </div>

              <div>
                <label htmlFor="summary" className="block text-sm font-medium text-gray-700 mb-2">
                  Summary *
                </label>
                <textarea
                  id="summary"
                  name="summary"
                  value={formData.summary}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Brief summary of the article"
                  required
                />
              </div>

              <div>
                <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
                  Article Content *
                </label>
                <textarea
                  id="content"
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  rows={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Write your article content here..."
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  Use markdown formatting for better structure (## for headings, **bold**, *italic*, etc.)
                </p>
              </div>

              <div>
                <label htmlFor="categories" className="block text-sm font-medium text-gray-700 mb-2">
                  Categories *
                </label>
                <CategoryAutocomplete
                  selectedCategories={selectedCategories}
                  onCategoriesChange={setSelectedCategories}
                  placeholder="Search and select categories..."
                  className="mb-4"
                />
                {selectedCategories.length === 0 && (
                  <p className="text-sm text-red-500 mt-1">Please select at least one category</p>
                )}
              </div>

              <div>
                <label htmlFor="hashtags" className="block text-sm font-medium text-gray-700 mb-2">
                  Hashtags
                </label>
                <HashtagAutocomplete
                  selectedHashtags={selectedHashtags}
                  onHashtagsChange={setSelectedHashtags}
                  placeholder="Add hashtags (press Enter to create new ones)..."
                />
              </div>

              <div>
                <label htmlFor="sources" className="block text-sm font-medium text-gray-700 mb-2">
                  Sources
                </label>
                <textarea
                  id="sources"
                  name="sources"
                  value={formData.sources}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="List your sources and references here..."
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="guidelines"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  required
                />
                <label htmlFor="guidelines" className="ml-2 block text-sm text-gray-700">
                  I agree to follow Grokypedia&apos;s <Link href="/guidelines" className="text-blue-600 hover:underline">content guidelines</Link> and <Link href="/terms" className="text-blue-600 hover:underline">terms of service</Link> *
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting...' : 'Submit Article'}
              </button>
            </form>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Guidelines */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-4">📝 Content Guidelines</h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li>• Write in a neutral, encyclopedic tone</li>
              <li>• Use reliable, verifiable sources</li>
              <li>• Avoid personal opinions or bias</li>
              <li>• Include proper citations</li>
              <li>• Keep content factual and accurate</li>
            </ul>
          </div>

          {/* Ways to Contribute */}
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">🤝 Ways to Contribute</h3>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-start space-x-2">
                <span className="text-green-600">✓</span>
                <span>Write new articles on topics you know</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-green-600">✓</span>
                <span>Improve existing articles</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-green-600">✓</span>
                <span>Fix grammatical errors</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-green-600">✓</span>
                <span>Add citations and sources</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-green-600">✓</span>
                <span>Review and fact-check content</span>
              </div>
            </div>
          </div>


          {/* Contact */}
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">💬 Need Help?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Have questions about contributing? We&apos;re here to help!
            </p>
            <Link 
              href="/contact"
              className="text-blue-600 hover:underline text-sm"
            >
              Contact our support team →
            </Link>
          </div>
        </div>
      </div>

      {/* Success Dialog */}
      {success && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md mx-4 shadow-xl">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                מאמר פורסם בהצלחה!
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                המאמר &quot;{newArticleTitle}&quot; פורסם ונעשה זמין לציבור באופן מיידי. תודה על תרומתך לגרוקיפדיה!
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setSuccess(false);
                    router.push(`/wiki/${encodeURIComponent(newArticleTitle)}`);
                  }}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
                >
                  צפה במאמר
                </button>
                <button
                  onClick={() => setSuccess(false)}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 transition-colors"
                >
                  סגור
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
